import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import {
  isOptimisticComment,
  useCreateAgentActionComment,
  useDeleteAgentActionComment,
} from "@/accounts-payable/query";
import type {
  ApAgentAction,
  ApAgentActionComment,
} from "@/accounts-payable/types";
import { SectionLabel } from "./shared";
import { ZonedDateTime } from "./zoned-date-time";

const CommentItem = ({
  comment,
  canDelete,
  onDelete,
  deleting,
}: {
  comment: ApAgentActionComment;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
}) => (
  <Stack
    className="w-full"
    direction="row"
    gap={2}
    align="start"
    justify="between"
  >
    <Stack align="start" gap={0} className="min-w-0 flex-1">
      <Stack direction="row" gap={2} align="baseline" className="flex-wrap">
        <span className="text-sm font-semibold">
          {comment.author ?? "Unknown"}
        </span>
        <Text variant="caption" tone="muted">
          <ZonedDateTime value={comment.created_at} />
        </Text>
      </Stack>
      <span className="text-sm whitespace-pre-wrap text-subtle">
        {comment.content}
      </span>
    </Stack>
    {canDelete && (
      <Button
        variant="ghost"
        color="danger"
        size="icon-xs"
        aria-label="Delete comment"
        onClick={onDelete}
        disabled={deleting}
        className="mt-0.5"
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    )}
  </Stack>
);

/**
 * Reviewer comments thread + composer for a single agent action. The composer
 * rests as a one-line field (the height of the action pills) and grows to two
 * rows on focus; Cancel / Comment appear only once there's text, so a list of
 * actions isn't a stack of empty textareas. Escape leaves the field but keeps
 * the draft. Comments are
 * embedded on the action by the runs serializer, so we read `action.comments`;
 * the create/delete mutations optimistically update and refresh the runs cache.
 */
export const ActionComments = ({
  action,
  businessId,
}: {
  action: ApAgentAction;
  businessId: string | null;
}) => {
  const comments = action.comments ?? [];
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);

  const create = useCreateAgentActionComment(businessId);
  const remove = useDeleteAgentActionComment(businessId);

  const submit = () => {
    const content = draft.trim();
    if (!content) return;
    create.mutate(
      { actionId: action.id, content },
      {
        onSuccess: () => setDraft(""),
        onError: () => toast.error("Couldn't post comment. Please try again."),
      },
    );
  };

  const deleteComment = (commentId: string) =>
    remove.mutate(
      { actionId: action.id, commentId },
      {
        onError: () =>
          toast.error("Couldn't delete comment. Please try again."),
      },
    );

  const cancel = () => setDraft("");
  const hasDraft = draft.trim().length > 0;
  // Open while in use or holding unsent text; one line otherwise.
  const expanded = focused || draft.length > 0;

  return (
    <Stack className="w-full" align="start" gap={2}>
      {comments.length > 0 && (
        <Stack className="w-full" align="start" gap={2}>
          <SectionLabel>Comments ({comments.length})</SectionLabel>
          <Stack className="w-full" align="start" gap={4}>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                canDelete={
                  // Server decides ownership (can_delete); the optimistic guard
                  // just holds off until the comment has a real id to delete.
                  comment.can_delete && !isOptimisticComment(comment)
                }
                deleting={remove.isPending}
                onDelete={() => deleteComment(comment.id)}
              />
            ))}
          </Stack>
        </Stack>
      )}

      <Stack className="w-full" gap={1} align="end">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          // Escape only leaves the field; the text stays until Cancel, so a
          // half-written note isn't lost to a stray key.
          onKeyDown={(e) => {
            if (e.key === "Escape") e.currentTarget.blur();
          }}
          placeholder="Add a comment…"
          aria-label="Comment"
          rows={expanded ? 2 : 1}
          // Sized down from core's 8rem / 15px form field to sit in a dense
          // half-step py-1.5 is deliberate: 16px line + 2px border + 12px
          // fits 32px; py-2 would overflow it.
          className={cn("min-h-0 resize-none py-1.5", { "h-8": !expanded })}
        />
        {hasDraft && (
          <Stack direction="row" gap={1} align="center">
            <Button
              variant="ghost"
              size="sm"
              onClick={cancel}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            {/* The label carries the in-flight state, so there's a visible
                change while the comment posts. */}
            <Button
              variant="link"
              size="sm"
              onClick={submit}
              disabled={create.isPending}
              aria-busy={create.isPending || undefined}
            >
              {create.isPending ? "Posting…" : "Comment"}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
